import React from 'react';
import { useLoginContext } from '../../Contexts/LoginContext';
import { useLocation } from 'react-router-dom';
import { useStoreContext } from '../../Contexts/StoreContext';
import { TbArrowBigRightFilled, TbChecklist, TbShoppingBag } from 'react-icons/tb';
import { useCartContext } from '../../Contexts/CartContext';
import SelectedProduct from './Product/SelectedProduct';
import { useCheckoutContext } from '../../Contexts/CheckoutContext';
import BadgeIndicator from './Product/BadgeIndicator';
import { useStoreHook } from '../../Hooks/StoreHook';
import BoxContentStoreOrderTypeHeader from './BoxContentStoreOrderTypeHeader';
import BoxContentSearchHeader from './BoxContentSearchHeader';
import HeaderLogo from './HeaderLogo';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { HiBars3 } from 'react-icons/hi2';
import Button from '../Form/Button';
import { useTranslation } from 'react-i18next';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import ButtonLogin from './ButtonLogin';
import BoxContentTranslate from './BoxContentTranslate';
import ButtonPrimary from '../Form/ButtonPrimary';
import { useOrderHook } from '../../Hooks/OrderHook';
import ButtonOpenStoreCounter from './ButtonOpenStoreCounter';
import { formatCartId } from '../../Utils/Formatters';

const Header = () => {

    const { isAuthenticated, isAuthenticatedToQuickPurchase, isAuthenticatedBySSO } = useLoginContext();
    const { currentStore, cashier } = useStoreContext();
    const { isMenu, isStoreCounter } = useOrderTypeContext();
    const { isMobile, setShowMainMenu, setShowSearchBar, setShowTodayOrders } = useLayoutContext();
    const { t } = useTranslation();

    const { currentCart, loadingCurrentCart, loadingUpdateQuantity, getTotalQuantityProductsInConference, currentCartHasItems, cartDisplay, cartFinished, setShowCartConference, cartConference } = useCartContext();
    const { awaitingOrPendingCount, order } = useCheckoutContext();
    const { getLinkToHome } = useStoreHook();

    const location = useLocation();
    const isHome = location.pathname === getLinkToHome();
    const isProductPage = new RegExp(`^${getLinkToHome().replace('/', '\\/')}/produto/`).test(location.pathname);
    const { handleShowCart} = useOrderHook();

    const handleShowOrdersToday = () => {
        setShowSearchBar(false);
        setShowTodayOrders(true);
    }

    return (
        <header id="boxHeader" className="flex flex-col w-full justify-between sticky bg-custom-header top-0 z-30 text-custom-header-text">
            <section className='flex justify-center h-20 shadow-sm shadow-black/5'>
                <div className="flex p-4 items-center justify-between w-full md:max-w-[1460px] gap-4 md:gap-6">

                    {/* LOGO */}
                    <HeaderLogo className='min-h-20' />

                    <div className='flex items-center justify-end w-full gap-4 md:gap-6'>

                        {/* SEARCH */}
                        <BoxContentSearchHeader show={currentStore && (isHome || isProductPage)} />

                        <div className="flex items-center gap-2 md:gap-4 h-full">
                            <BoxContentStoreOrderTypeHeader sizeButton={22} />

                            <div className="hidden md:flex items-center gap-2 h-full md:-me-2">
                                {!isMenu && !isStoreCounter && (
                                    <>
                                        <Button rounded gray onClick={handleShowOrdersToday} disabled={!isAuthenticated && !isAuthenticatedToQuickPurchase}>
                                            <div className='relative'>
                                                <TbChecklist size={23} className='zoomInIconNoDelay' />
                                                { awaitingOrPendingCount > 0 && <BadgeIndicator quantity={awaitingOrPendingCount} className='absolute -top-3.5 -right-3.5 bg-shopfood-title animate-bounce' />}
                                            </div>
                                        </Button>

                                        <div className='relative'>
                                            <Button
                                                rounded
                                                gray
                                                disabled={!currentCartHasItems}
                                                onClick={handleShowCart}
                                                className='text-custom-primary disabled:text-gray-600'>
                                                <TbShoppingBag size={23} className='shrink-0' />
                                                <SelectedProduct alwaysNotify={false} loading={loadingCurrentCart || loadingUpdateQuantity} animated={false} quantity={currentCart ? getTotalQuantityProductsInConference(currentCart.items) : 0} className="rounded-lg -top-0.5 -right-0.5" />
                                            </Button>
                                        </div>

                                        <ButtonLogin
                                            asButtonGray
                                            forceSsoLogin={isAuthenticatedBySSO}
                                            hideLabelOnMobile
                                            withLoading={isAuthenticatedBySSO}
                                            className='text-nowrap'
                                            labelLink={t('login.label_button_login')}
                                        />
                                    </>
                                )}

                                {isStoreCounter && cashier && currentStore.customer_self_attendance &&
                                    <>
                                        {!isMobile && cartDisplay
                                            ?
                                                cartConference.length > 0 ?
                                                    <div className='flex items-center gap-1'>
                                                        <ButtonPrimary onClick={() => setShowCartConference(true)} className='relative text-nowrap w-full md:max-w-max gap-2 items-center justify-between'>
                                                            <span className='flex gap-1 items-center'>
                                                                <TbArrowBigRightFilled size={20} className='animate-side text-white' />
                                                                <span>{getTotalQuantityProductsInConference(cartConference) > 1 ? `${getTotalQuantityProductsInConference(cartConference)} ${t('layout.item_label_plural')}` : `${getTotalQuantityProductsInConference(cartConference)} ${t('layout.item_label_singular')}`}</span>
                                                            </span>
                                                        </ButtonPrimary>
                                                    </div>
                                                :
                                                    <ButtonPrimary outline onClick={() => handleShowCart(order)} className='hidden md:flex ring-inset shadow-reverse-md md:shadow-none'>
                                                        <span className='font-extrabold text-nowrap'>nº { formatCartId(cartDisplay?.display_id || cartDisplay?.id) }</span>
                                                    </ButtonPrimary>
                                            :
                                                <div className='w-auto hidden md:flex text-sm'>
                                                    <ButtonOpenStoreCounter className='text-nowrap' forceLabel label={cartFinished ? '' : 
                                                        <div className='flex flex-col'>
                                                            <span className='leading-none text-xl font-black leading-none'>{t('menu.mobile.store_counter.button_open_label')}</span>
                                                            <span className='text-xs opacity-75 font-medium leading-none'>{t('store_counter.name')}</span>
                                                        </div>} />
                                                </div>
                                        }
                                    </>
                                }

                                <Button
                                    rounded
                                    gray
                                    onClick={() => {
                                        React.startTransition(() => {
                                            setShowMainMenu(true);
                                        });
                                    }}
                                    className='hidden md:flex'>
                                    <div><HiBars3 size={23} /></div>
                                </Button>

                            </div>

                            <BoxContentTranslate onlyFlags rounded className='border-none py-0' />

                        </div>

                    </div>
                </div>
            </section>

        </header>
    );
};

export default Header;
